import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Users,
  ChartBar
} from 'lucide-react';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { getProfilePlaceholder, getCoverPlaceholder } from '../../utils/placeholders';
import InviteInfluencerModal from '../../components/brand/InviteInfluencerModal';
import messageService from '../../services/messageService';
import { getPortfolioItems } from '../../services/portfolioService';
import reviewService from '../../services/reviewService';
import LoadingSpinner from '../../components/LoadingSpinner';
import ReportFlag from '../../components/common/ReportFlag';
import Breadcrumb from '../../components/common/Breadcrumb';

const InfluencerProfile = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('socialmedia');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [invitationSent, setInvitationSent] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  
  // Mock data for demonstration
  const mockInfluencers = [
    {
      id: 'inf1',
      name: 'Maria Santos',
      username: '@mariasantos',
      avatar: 'https://randomuser.me/api/portraits/women/1.jpg',
      coverImage: 'https://images.unsplash.com/photo-1522441815192-d9f04eb0615c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      category: 'Fashion',
      platforms: ['Instagram', 'TikTok'],
      followers: {
        instagram: 85000,
        tiktok: 120000,
        total: 205000
      },
      engagementRate: '4.2%',
      location: 'Manila',
      bio: 'Fashion and lifestyle content creator. Sharing my daily outfits and beauty tips.',
      previousCampaigns: 8,
      rating: 4.8,
      email: 'maria@example.com',
      phone: '+63 912 345 6789',
      demographics: {
        ageRange: '18-34 (78%)',
        gender: 'Female (65%), Male (35%)',
        topLocations: ['Philippines', 'United States', 'Singapore']
      },
      portfolio: [
        {
          id: 'portfolio-1',
          title: 'Summer Fashion Collection',
          category: 'Brand Collaboration',
          description: 'Collaboration with SummerStyle for their new summer collection. Featured their beachwear line in a series of Instagram posts and Stories.',
          link: 'https://instagram.com/p/sample-post-1',
          featured: true,
          coverImage: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
          images: [
            'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
            'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60'
          ]
        },
        {
          id: 'portfolio-2',
          title: 'Eco-Friendly Skincare Review',
          category: 'Product Review',
          description: 'In-depth review of NatureGlow\'s new eco-friendly skincare line. Tested their products for two weeks and shared my honest experience.',
          link: 'https://instagram.com/p/sample-post-2',
          featured: false,
          coverImage: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
          images: []
        },
        {
          id: 'portfolio-3',
          title: 'Travel Vlog: Palawan',
          category: 'Video Content',
          description: 'A week-long adventure in Palawan, showcasing the beautiful beaches, local cuisine, and hidden gems of the island.',
          link: 'https://youtube.com/watch?v=sample-video',
          featured: true,
          coverImage: 'https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
          images: [
            'https://images.unsplash.com/photo-1520454974749-611b7248ffdb?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60'
          ]
        }
      ]
    },
    // Add more mock influencers as needed
  ];

  useEffect(() => {
    // Fetch real influencer data from Firestore
    const fetchInfluencerData = async () => {
      if (!id) return;
      
      try {
        // Get influencer document from Firestore
        const influencerRef = doc(db, 'users', id);
        const influencerSnap = await getDoc(influencerRef);
        
        if (influencerSnap.exists()) {
          // Get influencer data
          const influencerData = influencerSnap.data();

          // Check privacy settings — default to visible if not explicitly set to false
          const privacySettings = influencerData.settings?.privacy || {};
          if (privacySettings.publicProfile === false) {
            setInfluencer(null);
            setLoading(false);
            return;
          }

          // Extract influencer details safely
          const influencerDetails = influencerData.influencerDetails || {};
          const socialMedia = influencerData.socialMedia || {}; // Read from socialMedia field
          const followers = influencerData.followers || {}; // Read followers from main user document
          
          // Calculate total followers if not provided
          const totalFollowers = followers.total || 
            Object.values(followers).reduce((sum, count) => sum + (Number(count) || 0), 0);
          
          // Determine available platforms from social media URLs
          const platforms = [];
          if (socialMedia.instagram) platforms.push('Instagram');
          if (socialMedia.tiktok) platforms.push('TikTok');
          if (socialMedia.facebook) platforms.push('Facebook');
          if (socialMedia.twitter) platforms.push('Twitter');
          
          // Create a formatted influencer object with proper fallbacks
          const formattedInfluencer = {
            id: influencerSnap.id, // This is the document ID from Firestore
            uid: influencerData.uid || influencerSnap.id, // Use uid if available, otherwise use document ID
            name: influencerData.displayName || 'Unknown',
            username: influencerData.username || '@' + (influencerData.displayName || '').toLowerCase().replace(/\s+/g, ''),
            // Try multiple possible image field names with a default fallback
            avatar: influencerData.photoURL || 
                   influencerData.profileImageUrl || 
                   influencerData.profileImage || 
                   influencerData.avatar || 
                   getProfilePlaceholder(influencer?.displayName || 'User'),
            coverImage: influencerData.coverImageUrl || 
                       influencerData.coverImage || 
                       getCoverPlaceholder(),
            category: influencerDetails.category || influencerData.category || 'Influencer',
            platforms: platforms.length > 0 ? platforms : ['Instagram'],
            followers: {
              ...followers,
              total: totalFollowers
            },
            engagementRate: influencerDetails.engagementRate || influencerData.engagementRate || '0%',
            location: influencerDetails.location || influencerData.location || 'Philippines',
            bio: influencerDetails.bio || influencerData.bio || 'No bio available',
            previousCampaigns: influencerDetails.previousCampaigns || influencerData.previousCampaigns || 0,
            rating: influencerDetails.rating || influencerData.rating || 0,
            email: influencerData.email || '',
            phone: influencerData.phone || '',
            // Add age and gender fields directly to the influencer object
            age: influencerData.age || null,
            gender: influencerData.gender || null,
            audienceDemographics: influencerData.audienceDemographics || null,
            followers: followers, // Add followers data to the influencer object
            demographics: influencerDetails.demographics || {
              ageRange: 'Not specified',
              gender: 'Not specified',
              topLocations: ['Philippines']
            },
            isAffiliate: influencerData.isAffiliate || null,
            monthlyGMV: influencerData.monthlyGMV || 0,
            gmvProofUrl: influencerData.gmvProofUrl || null,
            socialMedia: socialMedia, // Add socialMedia data to the influencer object
            // Privacy settings (for conditional rendering)
            allowBrandInquiries: privacySettings.allowBrandInquiries !== false,
            showEarnings: privacySettings.showEarnings === true
            // Remove the portfolio property as we'll fetch it separately
          };
          

          setInfluencer(formattedInfluencer);

          // After setting the influencer data, fetch their portfolio items
          fetchPortfolioItems(influencerData.uid || influencerSnap.id);

          // Fetch real average rating from reviews
          try {
            const ratingData = await reviewService.getAverageRating(influencerData.uid || influencerSnap.id, 'influencer');
            if (ratingData && ratingData.totalReviews > 0) {
              setInfluencer(prev => prev ? { ...prev, rating: parseFloat(ratingData.averageRating.toFixed(1)), totalReviews: ratingData.totalReviews } : prev);
            }
          } catch (e) {
            // Rating fetch is non-critical
          }
        } else {

          setInfluencer(null);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching influencer data:', error);
        setLoading(false);
      }
    };
    
    // Separate function to fetch portfolio items from portfolioItems collection
    const fetchPortfolioItems = async (userId) => {
      try {
        setPortfolioLoading(true);

        const items = await getPortfolioItems(userId);

        setPortfolioItems(items);
      } catch (error) {
        console.error('Error fetching portfolio items:', error);
      } finally {
        setPortfolioLoading(false);
      }
    };

    fetchInfluencerData();
  }, [id]);

  const handleInvite = async (invitationData) => {
    try {
      if (!currentUser || !influencer) {
        throw new Error('Missing user information');
      }
      
      // 1. Save the invitation to Firestore
      const invitationsRef = collection(db, 'campaignInvitations');
      const invitationRef = await addDoc(invitationsRef, {
        ...invitationData,
        brandId: currentUser.uid,
        influencerId: influencer.id, // Document ID for reference
        influencerUid: influencer.uid || influencer.id, // Use uid for authentication purposes
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // 2. Send a message to the influencer about the invitation
      const campaignName = invitationData.campaignName || 'a campaign';
      const messageText = `You've been invited to participate in "${campaignName}"! ${invitationData.message}`;
      
      // Log the IDs being used for debugging
      // Message IDs prepared
      
      await messageService.sendMessage(
        currentUser.uid,                // Sender (brand)
        influencer.uid || influencer.id, // Receiver (influencer) - use uid if available
        messageText,                    // Message text
        [],                             // No attachments
        invitationData.campaignId       // Campaign ID
      );
      

      setInvitationSent(true);
      setInviteError(null);
      
      // Show success message temporarily
      setTimeout(() => {
        setInvitationSent(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error sending invitation:', error);
      setInviteError(error.message || 'Failed to send invitation');
      
      // Clear error after a few seconds
      setTimeout(() => {
        setInviteError(null);
      }, 5000);
    }
  };

  if (loading) {
    return null;
  }

  if (!influencer) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Influencer Not Found</h3>
        <p className="text-sm text-gray-600 mb-4">The influencer you're looking for doesn't exist or has been removed.</p>
        <Breadcrumb
          items={[
            { label: 'Influencers', to: '/brand/influencers' },
            { label: 'Influencer Profile' },
          ]}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Cover and Profile */}
      <div className="relative mb-8">
        <div className="h-48 w-full bg-gray-200 rounded-lg overflow-hidden">
          {influencer.coverImage ? (
            <img
              src={influencer.coverImage}
              alt={`${influencer.name} cover`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-blue-100 to-primary-light"></div>
          )}
        </div>
        
        <div className="absolute bottom-0 left-8 transform translate-y-1/2 flex items-end">
          <div className="relative">
            <img
              src={influencer.avatar}
              alt={influencer.name}
              className="h-24 w-24 rounded-full border-4 border-white object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.onerror = null;
                e.target.src = getProfilePlaceholder(influencer?.displayName || 'User');
              }}
            />
            <div className="absolute bottom-0 right-0 h-5 w-5 rounded-full border-2 border-white bg-green-400"></div>
          </div>
        </div>
        
        {/* Removed Invite to Campaign button */}
      </div>
      
      {/* Profile Info */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <div className="mb-4 md:mb-0">
              <h1 className="text-2xl font-bold text-gray-900">{influencer.name}</h1>
              <p className="text-gray-600">{influencer.username}</p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {influencer.category}
              </span>
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(influencer.rating) ? 'text-yellow-400' : 'text-gray-300'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 15.585l-7.071 3.535 1.357-7.9-5.714-5.573 7.9-1.143L10 0l3.528 7.143 7.9 1.143-5.714 5.572 1.357 7.9z"
                      clipRule="evenodd"
                    />
                  </svg>
                ))}
                <span className="ml-1 text-sm text-gray-500">({influencer.rating})</span>
              </div>
              <ReportFlag
                targetType="influencer"
                targetId={influencer.id || id}
                targetName={influencer.name}
                buttonStyle="text"
                triggerLabel="Report"
              />
            </div>
          </div>
          
          <div className="mt-6">
            <p className="text-sm text-gray-700">{influencer.bio}</p>
          </div>
          
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-4">
              {invitationSent && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Invitation Sent
                </span>
              )}
              {inviteError && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ml-2">
                  {inviteError}
                </span>
              )}
            </div>

          </div>
          
          {/* Age and Gender Information */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Personal Info</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-500">Age:</span>
                <p className="text-sm font-medium text-gray-900">
                  {influencer.age || 'Not specified'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Gender:</span>
                <p className="text-sm font-medium text-gray-900 capitalize">
                  {influencer.gender || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Audience Insights */}
          {influencer.audienceDemographics && (() => {
            const ad = influencer.audienceDemographics;
            const hasAgeData = ad.ageRanges && Object.values(ad.ageRanges).some(v => parseInt(v) > 0);
            const hasGenderData = ad.genderSplit && Object.values(ad.genderSplit).some(v => parseInt(v) > 0);
            const hasLocations = ad.topLocations && ad.topLocations.filter(l => l).length > 0;
            const hasInterests = ad.interests && ad.interests.length > 0;

            if (!hasAgeData && !hasGenderData && !hasLocations && !hasInterests) return null;

            return (
              <div className="mt-6 border-t border-gray-100 pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  Audience Insights
                </h3>
                <p className="text-xs text-gray-400 mb-4">Based on influencer-reported data</p>

                {/* Age Range Distribution */}
                {hasAgeData && (
                  <div className="mb-5">
                    <h4 className="text-xs font-medium text-gray-600 mb-2">Age Range Distribution</h4>
                    <div className="flex h-4 rounded-full overflow-hidden bg-gray-200">
                      {Object.entries(ad.ageRanges).map(([range, val]) => {
                        const pct = parseInt(val) || 0;
                        if (pct === 0) return null;
                        const colors = { '18-24': 'bg-blue-400', '25-34': 'bg-blue-500', '35-44': 'bg-purple-400', '45-54': 'bg-purple-500', '55+': 'bg-pink-400' };
                        return <div key={range} className={colors[range]} style={{ width: `${pct}%` }} title={`${range}: ${pct}%`} />;
                      })}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {Object.entries(ad.ageRanges).map(([range, val]) => {
                        const pct = parseInt(val) || 0;
                        if (pct === 0) return null;
                        const colors = { '18-24': 'bg-blue-400', '25-34': 'bg-blue-500', '35-44': 'bg-purple-400', '45-54': 'bg-purple-500', '55+': 'bg-pink-400' };
                        return (
                          <span key={range} className="inline-flex items-center text-xs text-gray-600">
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[range]} mr-1.5`}></span>
                            {range}: {pct}%
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Gender Split */}
                {hasGenderData && (
                  <div className="mb-5">
                    <h4 className="text-xs font-medium text-gray-600 mb-2">Gender Split</h4>
                    <div className="flex h-4 rounded-full overflow-hidden bg-gray-200">
                      {Object.entries(ad.genderSplit).map(([gender, val]) => {
                        const pct = parseInt(val) || 0;
                        if (pct === 0) return null;
                        const colors = { male: 'bg-blue-500', female: 'bg-pink-500', other: 'bg-purple-400' };
                        return <div key={gender} className={colors[gender]} style={{ width: `${pct}%` }} title={`${gender}: ${pct}%`} />;
                      })}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {Object.entries(ad.genderSplit).map(([gender, val]) => {
                        const pct = parseInt(val) || 0;
                        if (pct === 0) return null;
                        const colors = { male: 'bg-blue-500', female: 'bg-pink-500', other: 'bg-purple-400' };
                        return (
                          <span key={gender} className="inline-flex items-center text-xs text-gray-600 capitalize">
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[gender]} mr-1.5`}></span>
                            {gender}: {pct}%
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Top Locations */}
                {hasLocations && (
                  <div className="mb-5">
                    <h4 className="text-xs font-medium text-gray-600 mb-2">Top Locations</h4>
                    <div className="flex flex-wrap gap-2">
                      {ad.topLocations.filter(l => l).map((location, idx) => (
                        <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {location}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Interests */}
                {hasInterests && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-600 mb-2">Audience Interests</h4>
                    <div className="flex flex-wrap gap-2">
                      {ad.interests.map((interest, idx) => (
                        <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Platforms</h3>
            <div className="flex flex-wrap">
              {influencer.platforms.map((platform) => (
                <span key={platform} className="mr-2 mb-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  {platform}
                </span>
              ))}
            </div>
          </div>
          
          {/* Affiliate & Performance Information */}

          <div className="mt-8 border-t border-gray-100 pt-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center">
              <ChartBar className="h-4 w-4 mr-2" />
              Affiliate & Performance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg flex flex-col justify-between">
                <span className="text-xs text-gray-500 uppercase font-semibold">Affiliate Creator</span>
                <p className="mt-1 text-sm font-bold text-gray-900 capitalize">
                  {influencer.isAffiliate ? (influencer.isAffiliate === 'yes' ? '✅ Yes' : '❌ No') : 'Not specified'}
                </p>
              </div>
              
              {influencer.showEarnings && (
                <div className="bg-gray-50 p-4 rounded-lg flex flex-col justify-between">
                  <span className="text-xs text-gray-500 uppercase font-semibold">Avg. Monthly GMV</span>
                  <p className="mt-1 text-sm font-bold text-gray-900">
                    {influencer.monthlyGMV ? `₱${Number(influencer.monthlyGMV).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '₱0.00'}
                  </p>
                </div>
              )}

              {influencer.showEarnings && influencer.gmvProofUrl && (
                <div className="bg-gray-50 p-4 rounded-lg flex flex-col justify-between">
                  <span className="text-xs text-gray-500 uppercase font-semibold">Verification</span>
                  <div className="mt-1">
                    <a 
                      href={influencer.gmvProofUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-bold text-primary hover:text-primary-dark"
                    >
                      View GMV Proof
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'socialmedia'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('socialmedia')}
            >
              Social Media
            </button>

            <button
              className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'portfolio'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('portfolio')}
            >
              Portfolio
            </button>
          </nav>
        </div>
        
        <div className="p-6">


          {activeTab === 'socialmedia' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">Social Media Profiles</h3>
              
              {influencer.socialMedia ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {influencer.socialMedia.facebook && (
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900">Facebook</h4>
                      </div>
                      <a 
                        href={influencer.socialMedia.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 break-all mb-2 block"
                      >
                        {influencer.socialMedia.facebook}
                      </a>
                      {influencer.followers && influencer.followers.facebook > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{influencer.followers.facebook.toLocaleString()} followers</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {influencer.socialMedia.instagram && (
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.875 2.026-1.297 3.323-1.297s2.448.422 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.608c-.384 0-.735-.147-.997-.49-.262-.343-.384-.735-.384-1.176 0-.49.147-.875.49-1.176.262-.262.613-.384.997-.384.49 0 .875.122 1.176.384.343.301.49.686.49 1.176 0 .441-.147.833-.49 1.176-.301.343-.686.49-1.176.49z"/>
                          </svg>
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">Instagram</h4>
                      </div>
                      <a 
                        href={influencer.socialMedia.instagram} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 break-all mb-2 block"
                      >
                        {influencer.socialMedia.instagram}
                      </a>
                      {influencer.followers && influencer.followers.instagram > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{influencer.followers.instagram.toLocaleString()} followers</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {influencer.socialMedia.tiktok && (
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-.04-.1z"/>
                          </svg>
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">TikTok</h4>
                      </div>
                      <a 
                        href={influencer.socialMedia.tiktok} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-900 hover:text-gray-700 break-all mb-2 block"
                      >
                        {influencer.socialMedia.tiktok}
                      </a>
                      {influencer.followers && influencer.followers.tiktok > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{influencer.followers.tiktok.toLocaleString()} followers</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {influencer.socialMedia.twitter && (
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">X (Twitter)</h4>
                      </div>
                      <a 
                        href={influencer.socialMedia.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-900 hover:text-gray-700 break-all mb-2 block"
                      >
                        {influencer.socialMedia.twitter}
                      </a>
                      {influencer.followers && influencer.followers.twitter > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{influencer.followers.twitter.toLocaleString()} followers</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 p-8 rounded-lg text-center">
                  <p className="text-sm text-gray-500">This influencer hasn't added their social media profiles yet.</p>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'portfolio' && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-4">Portfolio Showcase</h3>
              
              {portfolioLoading ? (
                <div className="flex justify-center items-center h-32">
          <LoadingSpinner size="md" color="primary" />
        </div>
              ) : portfolioItems && portfolioItems.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {portfolioItems.map((item) => (
                    <div key={item.id} className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                      <div className="relative">
                        <img 
                          src={item.coverImage || getCoverPlaceholder()} 
                          alt={item.title} 
                          className="w-full h-48 object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.onerror = null;
                            e.target.src = getCoverPlaceholder();
                          }}
                        />
                        {item.featured && (
                          <div className="absolute top-2 left-2 bg-primary text-white text-xs font-medium px-2 py-1 rounded">
                            Featured
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-gray-500 mb-2">{item.category}</p>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                        
                        {item.link && (
                          <a 
                            href={item.link} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-flex items-center text-sm text-primary hover:text-primary-dark"
                          >
                            View Original Content
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 p-8 rounded-lg text-center" data-component-name="InfluencerProfile">
                  <p className="text-sm text-gray-500" data-component-name="InfluencerProfile">This influencer hasn't added any portfolio items yet.</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      
      {/* Invite Modal — only if brand inquiries are allowed */}
      {influencer?.allowBrandInquiries && (
        <InviteInfluencerModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          influencer={influencer}
          onInvite={handleInvite}
        />
      )}
    </div>
  );
};

export default InfluencerProfile;
